import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ALL_SPOTIFY_GENRES } from '../../constants/genres';
import SpotifyService from '../../services/spotify';
import '../../styles/search.css';

const POPULARITY_RANGES = [
  { value: 25, label: "Low Popularity", range: { min: 0, max: 25 } },
  { value: 50, label: "Medium-Low Popularity", range: { min: 26, max: 50 } },
  { value: 75, label: "Medium-High Popularity", range: { min: 51, max: 75 } },
  { value: 100, label: "High Popularity", range: { min: 76, max: 100 } }
];

const currentYear = new Date().getFullYear();

// Levenshtein Distance for similarity scoring
function levenshteinDistance(s1, s2) {
  const m = s1.length, n = s2.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (s1[i-1] === s2[j-1]) {
        dp[i][j] = dp[i-1][j-1];
      } else {
        dp[i][j] = Math.min(
          dp[i-1][j] + 1,
          dp[i][j-1] + 1,
          dp[i-1][j-1] + 1
        );
      }
    }
  }

  return dp[m][n];
}

// Custom similarity scoring
function calculateSimilarityScore(str1, str2) {
  const lowerStr1 = str1.toLowerCase();
  const lowerStr2 = str2.toLowerCase();
  
  // Exact match or starts with
  if (lowerStr1 === lowerStr2) return 1000;
  if (lowerStr1.startsWith(lowerStr2)) return 500;
  
  // Similarity based on Levenshtein distance
  return 100 - levenshteinDistance(lowerStr1, lowerStr2);
}

const Search = () => {
  const navigate = useNavigate();
  const [genre, setGenre] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [popularity, setPopularity] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [genreFocused, setGenreFocused] = useState(false);
  const genreRef = useRef(null);

  // Secure random number generator
  const getSecureRandom = useCallback((min, max) => {
    const range = max - min + 1;
    const bytesNeeded = Math.ceil(Math.log2(range) / 8);
    const maxNum = 256 ** bytesNeeded;
    const array = new Uint8Array(bytesNeeded);

    let randomNum;
    do {
      crypto.getRandomValues(array);
      randomNum = array.reduce((acc, byte, i) => acc + byte * (256 ** i), 0);
    } while (randomNum >= maxNum - (maxNum % range));

    return min + (randomNum % range);
  }, []);

  // Validate input
  const validateInput = useCallback(() => {
    if (selectedGenres.length === 0) {
      setError('Please select at least one genre');
      return false;
    }

    setError('');
    return true;
  }, [selectedGenres]);

  // Generates targeted offsets for popularity ranges
  const getTargetedOffsets = useCallback((popularity) => {
    const ranges = [
      { min: 0, max: 250 },     // Alta popolarità
      { min: 251, max: 500 },   // Medio-Alta popolarità
      { min: 501, max: 750 },   // Medio-Bassa popolarità
      { min: 751, max: 1000 }   // Bassa popolarità
    ];

    // Seleziona il range corrispondente alla popolarità
    const range = popularity <= 25 ? ranges[3] :
                  popularity <= 50 ? ranges[2] :
                  popularity <= 75 ? ranges[1] : 
                  ranges[0];

    // Genera 3 offset casuali nel range specificato
    const offsets = new Set();
    while (offsets.size < 3) {
      offsets.add(Math.floor(Math.random() * (range.max - range.min + 1)) + range.min);
    }
    return Array.from(offsets);
  }, []);

  // Genre suggestions and filtering
  useEffect(() => {
    if (genre) {
      const matches = ALL_SPOTIFY_GENRES
        .filter(g => g.toLowerCase().includes(genre.toLowerCase()))
        .sort((a, b) => {
          const scoreA = calculateSimilarityScore(a, genre);
          const scoreB = calculateSimilarityScore(b, genre);
          return scoreB - scoreA;
        });
      
      setSuggestions(matches);
    } else {
      setSuggestions([]);
    }
  }, [genre]);

  // Outside click handler for suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (genreRef.current && !genreRef.current.contains(event.target)) {
        setSuggestions([]);
        setGenreFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Add genre from suggestions
  const addGenre = useCallback((selectedGenre) => {
    if (!selectedGenres.includes(selectedGenre)) {
      setSelectedGenres(prev => [...prev, selectedGenre]);
      setGenre('');
      setSuggestions([]);
    }
  }, [selectedGenres]);

  // Remove genre from selected
  const removeGenre = useCallback((genreToRemove) => {
    setSelectedGenres(prev => 
      prev.filter(genre => genre !== genreToRemove)
    );
  }, []);

  // Main search handler
  const handleSearch = useCallback(async () => {
    if (!validateInput()) return;
    
    setLoading(true);
    setError('');

    // Delay function to prevent rate limiting
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    try {
      // Pool of artists to collect
      const finalArtists = [];
      
      // Search across selected genres
      for (const currentGenre of selectedGenres) {
        // Genera offset mirati per il livello di popolarità
        const offsets = getTargetedOffsets(popularity);
        
        // Unique artists tracking
        const uniqueArtists = new Set();
        
        for (const offset of offsets) {
          try {
            // Search tracks by genre
            const response = await SpotifyService.request('/search', {
              params: {
                q: `genre:${currentGenre}`,
                type: 'track',
                limit: 50,
                offset: offset
              }
            });

            // Extract artist IDs
            const artistIds = new Set();
            response.tracks.items.forEach(track => {
              track.artists.forEach(artist => {
                if (artist.id && !uniqueArtists.has(artist.id)) {
                  artistIds.add(artist.id);
                  uniqueArtists.add(artist.id);
                }
              });
            });

            // Batch get artist details
            if (artistIds.size > 0) {
              try {
                const artistsResponse = await SpotifyService.request(`/artists?ids=${Array.from(artistIds).join(',')}`, {
                  method: 'GET'
                });

                // Filter by popularity and add to final pool
                const filteredArtists = artistsResponse.artists
                  .filter(artist => 
                    !finalArtists.some(a => a.id === artist.id)
                  );

                finalArtists.push(...filteredArtists);

                // Stop if we have enough artists
                if (finalArtists.length >= 10) break;
              } catch (artistError) {
                // Log artist detail fetch error, but continue
                console.warn(`Error fetching artist details: ${artistError.message}`);
              }
            }

            // Add a small delay to prevent rate limiting
            await delay(200);

          } catch (searchError) {
            // Log search error, but continue with next offset
            console.warn(`Error in track search: ${searchError.message}`);
          }
        }

        // Final stop condition
        if (finalArtists.length >= 10) break;
      }

      // Fallback if no artists found
      if (finalArtists.length === 0) {
        setError('No artists found. Try broader search criteria.');
        return;
      }

      // Navigate to results
      navigate('/results', {
        state: {
          items: finalArtists.slice(0, 10),
          type: 'search'
        }
      });

    } catch (error) {
      console.error('Search failed:', error);
      setError('Search failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [
    validateInput, 
    selectedGenres, 
    getTargetedOffsets, 
    popularity, 
    navigate
  ]);

  return (
    <div className={`search-container ${genreFocused ? 'input-focused' : ''}`}>
      <button 
        onClick={() => navigate('/')}
        className="back-to-home"
      >
        <i className="bi bi-arrow-left" />
      </button>

      <div className={`search-content ${genreFocused ? 'input-focused' : ''}`}>
        {/* Genre Input with Chips */}
        <div className="search-input-container" ref={genreRef}>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            {selectedGenres.map(selectedGenre => (
              <span 
                key={selectedGenre} 
                className="badge bg-secondary d-flex align-items-center gap-1"
              >
                {selectedGenre}
                <button 
                  className="btn btn-sm btn-close" 
                  onClick={() => removeGenre(selectedGenre)}
                />
              </span>
            ))}
          </div>
          
          <input
            type="text"
            className="form-control"
            placeholder="Add genres..."
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            onFocus={() => setGenreFocused(true)}
            onBlur={() => {
              // Delay per permettere click sui suggerimenti
              setTimeout(() => {
                const activeElement = document.activeElement;
                const suggestionsContainer = genreRef.current?.querySelector('.list-group');
                const isClickingSuggestions = suggestionsContainer && suggestionsContainer.contains(activeElement);
                if (!isClickingSuggestions) {
                  setGenreFocused(false);
                }
              }, 200);
            }}
            autoComplete="off"
          />
          
          {genreFocused && suggestions.length > 0 && (
            <div className="list-group position-absolute" style={{zIndex: 1000, maxHeight: '200px', overflowY: 'auto'}}>
              {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                className="list-group-item list-group-item-action"
                onClick={() => {
                  addGenre(suggestion);
                  setGenreFocused(false);
                }}
                onMouseDown={(e) => e.preventDefault()} // Previeni blur prima del click
              >
                {suggestion}
              </button>
              ))}
            </div>
          )}
        </div>

        {/* Popularity Selector */}
        <div className={`popularity-container ${genreFocused ? 'hidden' : ''}`}>
            <div className="popularity-slider">
              <div 
                className="popularity-fill"
                style={{ width: `${popularity}%` }}
              />
              <div className="popularity-steps">
                {POPULARITY_RANGES.map((range, index) => (
                  <div
                    key={index}
                    className="popularity-step"
                    style={{ left: `${range.value}%` }}
                  />
                ))}
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={popularity}
                onChange={(e) => setPopularity(Number(e.target.value))}
                className="popularity-range"
              />
            </div>
            <div className="popularity-label">
              {POPULARITY_RANGES.find(range => popularity <= range.value)?.label}
            </div>
          </div>

        {error && !genreFocused && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className={`search-button-container ${genreFocused ? 'hidden' : ''}`}>
          <button
            className="search-button"
            disabled={selectedGenres.length === 0 || loading}
            onClick={handleSearch}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <i className="bi bi-search" />
                <span>Search</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Search;
